import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  History,
  Visibility,
  Refresh,
  CheckCircle,
  Warning,
  Schedule,
  Delete,
  Close
} from '@mui/icons-material';
import scanHistoryService from '../services/scanHistoryService';
import SkillHighlighting from './SkillHighlighting';

const ScanHistoryDropdown = ({ onSelectScan, onViewAll }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [showSkillHighlighting, setShowSkillHighlighting] = useState(false);
  const [selectedScanForSkills, setSelectedScanForSkills] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    loadRecentScans();
  };

  useEffect(() => {
    const onHistory = () => loadRecentScans();
    window.addEventListener('scanHistoryUpdated', onHistory);
    return () => window.removeEventListener('scanHistoryUpdated', onHistory);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteScan = async (scanId, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this scan? This action cannot be undone.')) {
      return;
    }

    try {
      await scanHistoryService.deleteScan(scanId);
      // Reload recent scans after deletion
      loadRecentScans();
    } catch (error) {
      setError('Failed to delete scan. Please try again.');
    }
  };

  

  const handleViewSkillHighlighting = (scan, event) => {
    event.stopPropagation();
    const normalized = {
      ...(scan.analysisResults || {}),
      resumeText: scan.resumeText || scan.inputData?.resumeText || '',
      jobDescription: scan.jobDescription || scan.inputData?.jobDescription || '',
      raw: scan.processingMetadata || {},
    };
    setSelectedScanForSkills(normalized);
    setShowSkillHighlighting(true);
  };

  const loadRecentScans = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await scanHistoryService.getRecentScans(5);
      setRecentScans(response.data || []);
    } catch (err) {
      setError('Failed to load recent scans');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectScan = (scan) => {
    onSelectScan(scan);
    handleClose();
  };

  const handleViewAll = () => {
    onViewAll();
    handleClose();
  };

  const formatScan = (scan) => {
    return scanHistoryService.formatScanForDisplay(scan);
  };

  const getScoreColor = (score) => scanHistoryService.getScoreColor(score);
  const formatRelativeTime = (dateString) => scanHistoryService.formatRelativeTime(dateString);

  return (
    <>
      <Tooltip title="Scan History">
        <IconButton
          onClick={handleClick}
          color="inherit"
          size="small"
        >
          <Badge badgeContent={recentScans.length} color="primary" max={9}>
            <History />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 400,
            maxHeight: 500,
            overflow: 'auto'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Recent Scans
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" onClick={loadRecentScans} disabled={loading}>
                <Refresh fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleClose}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Click to view full analysis
          </Typography>
        </Box>

        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" size="small">
              {error}
            </Alert>
          </Box>
        ) : recentScans.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <History sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No recent scans
            </Typography>
          </Box>
        ) : (
          recentScans.map((scan, index) => {
            const formattedScan = formatScan(scan);
            return (
              <MenuItem
                key={scan.scanId || index}
                onClick={() => handleSelectScan(scan)}
                sx={{ 
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getScoreColor(formattedScan.overallScore)
                    }}
                  />
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {formattedScan.jobTitle}
                      </Typography>
                      <Chip
                        label={`${formattedScan.overallScore}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          bgcolor: getScoreColor(formattedScan.overallScore),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formattedScan.company ? `${formattedScan.company} • ` : ''}{formattedScan.candidateName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Schedule sx={{ fontSize: 12 }} />
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(formattedScan.createdAt)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircle sx={{ fontSize: 12 }} />
                          <Typography variant="caption" color="text.secondary">
                            {formattedScan.preview.coveragePercent}%
                          </Typography>
                        </Box>
                        {formattedScan.preview.riskFlags > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Warning sx={{ fontSize: 12, color: 'warning.main' }} />
                            <Typography variant="caption" color="warning.main">
                              {formattedScan.preview.riskFlags}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                />
                
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={(e) => handleViewSkillHighlighting(scan, e)}
                  sx={{ ml: 1 }}
                  title="View Skill Matching"
                >
                  <Visibility fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => handleDeleteScan(scan.scanId, e)}
                  sx={{ ml: 1 }}
                  title="Delete Scan"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </MenuItem>
            );
          })
        )}

        <Divider />
        
        <MenuItem onClick={handleViewAll} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <Visibility />
          </ListItemIcon>
          <ListItemText>
            <Typography variant="body2" fontWeight={500}>
              View All Scans
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      

      {/* Skill Highlighting Modal */}
      <Dialog
        open={showSkillHighlighting}
        onClose={() => setShowSkillHighlighting(false)}
        maxWidth="xl"
        fullWidth
        aria-labelledby="skill-highlighting-modal"
      >
        <DialogContent sx={{ p: 0 }}>
          <SkillHighlighting
            scanData={selectedScanForSkills}
            onClose={() => setShowSkillHighlighting(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScanHistoryDropdown;
